import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Upload, FileText, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BulkUploadModal = ({ onClose, onSuccess }) => {
  const [jsonData, setJsonData] = useState('');
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [uploadType, setUploadType] = useState('json'); // 'json' or 'csv'
  const [csvFile, setCsvFile] = useState(null);

  const validateTreeData = (trees) => {
    const errors = [];
    const requiredFields = ['name', 'scientific_name', 'family', 'description', 'science_details', 'habitat', 'conservation_status', 'lat', 'lng', 'image_url'];

    trees.forEach((tree, index) => {
      requiredFields.forEach((field) => {
        if (!tree[field] && field !== 'lat' && field !== 'lng') {
          errors.push(`Tree ${index + 1}: Missing required field '${field}'`);
        }
      });

      if (tree.lat && (tree.lat < -90 || tree.lat > 90)) {
        errors.push(`Tree ${index + 1}: Latitude must be between -90 and 90`);
      }
      if (tree.lng && (tree.lng < -180 || tree.lng > 180)) {
        errors.push(`Tree ${index + 1}: Longitude must be between -180 and 180`);
      }
    });

    return errors;
  };

  const parseCSV = (csvText) => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) {
      throw new Error('CSV file must contain headers and at least one data row');
    }
    
    const headers = lines[0].split(',').map(h => h.trim());
    const trees = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const tree = {};
      
      headers.forEach((header, index) => {
        tree[header] = values[index];
      });
      
      trees.push(tree);
    }
    
    return trees;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationErrors([]);
    setLoading(true);

    try {
      let trees;
      
      if (uploadType === 'csv' && csvFile) {
        const reader = new FileReader();
        reader.onload = async (event) => {
          try {
            const csvText = event.target.result;
            trees = parseCSV(csvText);
            await processTrees(trees);
          } catch (error) {
            console.error('Error parsing CSV:', error);
            toast.error('Invalid CSV format. Please check your file.');
            setLoading(false);
          }
        };
        reader.readAsText(csvFile);
        return;
      } else {
        trees = JSON.parse(jsonData);
        
        if (!Array.isArray(trees)) {
          toast.error('Data must be an array of tree objects');
          setLoading(false);
          return;
        }
      }
      
      await processTrees(trees);

      await processTrees(trees);
    } catch (error) {
      console.error('Error parsing JSON:', error);
      toast.error('Invalid JSON format. Please check your data.');
      setLoading(false);
    }
  };

  const processTrees = async (trees) => {
    // Validate data
    const errors = validateTreeData(trees);
    if (errors.length > 0) {
      setValidationErrors(errors);
      toast.error(`Validation failed: ${errors.length} error(s) found`);
      setLoading(false);
      return;
    }

    // Upload trees one by one
    const token = localStorage.getItem('admin_token');
    let successCount = 0;
    let failCount = 0;

    for (const tree of trees) {
      try {
        await axios.post(
          `${API}/trees`,
          {
            name: tree.name,
            scientific_name: tree.scientific_name,
            family: tree.family,
            description: tree.description,
            science_details: tree.science_details,
            habitat: tree.habitat,
            conservation_status: tree.conservation_status,
            city: tree.city || '',
            lat: parseFloat(tree.lat),
            lng: parseFloat(tree.lng),
            image_url: tree.image_url,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        successCount++;
      } catch (error) {
        console.error(`Error uploading tree ${tree.name}:`, error);
        failCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`Successfully uploaded ${successCount} tree(s)${failCount > 0 ? `, ${failCount} failed` : ''}`);
      onSuccess();
    } else {
      toast.error('Failed to upload trees');
    }
    setLoading(false);
  };

  const exampleData = [
    {
      name: "Example Tree",
      scientific_name: "Eucalyptus example",
      family: "Myrtaceae",
      description: "A beautiful example tree.",
      science_details: "Scientific information about the tree.",
      habitat: "Coastal regions",
      conservation_status: "Least Concern",
      lat: -33.8688,
      lng: 151.2093,
      image_url: "https://example.com/tree.jpg"
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="bulk-upload-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Upload className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-heading font-bold text-primary">Bulk Upload Trees</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary rounded-lg transition-colors"
            data-testid="close-modal-button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6" data-testid="bulk-upload-form">
          {/* Upload Type Selector */}
          <div className="flex gap-4 mb-4">
            <button
              type="button"
              onClick={() => setUploadType('json')}
              className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors ${
                uploadType === 'json'
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-white text-muted-foreground hover:border-primary/50'
              }`}
            >
              JSON Format
            </button>
            <button
              type="button"
              onClick={() => setUploadType('csv')}
              className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors ${
                uploadType === 'csv'
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-white text-muted-foreground hover:border-primary/50'
              }`}
            >
              CSV File
            </button>
          </div>

          <div className="bg-accent/10 border border-accent/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-accent mt-0.5" />
              <div className="flex-1">
                <h3 className="font-medium text-foreground mb-2">
                  {uploadType === 'json' ? 'JSON Format Required' : 'CSV File Upload'}
                </h3>
                <p className="text-sm text-muted-foreground mb-3">
                  {uploadType === 'json'
                    ? 'Upload trees in JSON array format. Each tree must include all required fields.'
                    : 'Upload a CSV file with headers. First row must contain column names.'}
                </p>
                {uploadType === 'json' ? (
                  <details className="text-sm">
                    <summary className="cursor-pointer font-medium text-primary hover:underline mb-2">
                      View Example Format
                    </summary>
                    <pre className="bg-secondary/50 p-3 rounded text-xs overflow-x-auto">
                      {JSON.stringify(exampleData, null, 2)}
                      </pre>
                  </details>
                ) : (
                  <div className="text-sm">
                    <p className="font-medium text-primary mb-1">Required CSV Headers:</p>
                    <p className="text-xs text-muted-foreground font-mono">
                      name,scientific_name,family,description,science_details,habitat,conservation_status,city,lat,lng,image_url
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Input Area */}
          {uploadType === 'json' ? (
            <div>
              <label htmlFor="json-data" className="block text-sm font-medium text-foreground mb-2">
                Tree Data (JSON Array)
              </label>
              <textarea
                id="json-data"
                value={jsonData}
                onChange={(e) => setJsonData(e.target.value)}
                className="w-full h-64 px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
                placeholder="Paste your JSON array here..."
                data-testid="json-textarea"
                required
              />
            </div>
          ) : (
            <div>
              <label htmlFor="csv-file" className="block text-sm font-medium text-foreground mb-2">
                Select CSV File
              </label>
              <input
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={(e) => setCsvFile(e.target.files[0])}
                className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="csv-file-input"
                required
              />
              {csvFile && (
                <p className="text-sm text-muted-foreground mt-2">
                  Selected: {csvFile.name} ({(csvFile.size / 1024).toFixed(2)} KB)
                </p>
              )}
            </div>
          )}

          {validationErrors.length > 0 && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4" data-testid="validation-errors">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-destructive mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-medium text-destructive mb-2">Validation Errors</h3>
                  <ul className="text-sm text-destructive space-y-1 list-disc list-inside">
                    {validationErrors.slice(0, 10).map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                    {validationErrors.length > 10 && (
                      <li>...and {validationErrors.length - 10} more errors</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-border rounded-lg font-medium hover:bg-secondary transition-colors"
              data-testid="cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-50"
              data-testid="submit-button"
            >
              {loading ? 'Uploading...' : 'Upload Trees'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkUploadModal;